import React from 'react';
import { Descriptions, Typography, theme } from 'antd';
import { useTranslation } from 'react-i18next';
import type { Service } from '@/api/mcp-appointments';
import { serviceNeedsPrimaryResource } from '@/utils/serviceBooking';

const { Text, Paragraph } = Typography;

interface ServiceBookingSummaryProps {
  service: Service | null | undefined;
}

/** Read-only summary of the selected service's booking rules (duration, buffers, assignment, status). */
export const ServiceBookingSummary: React.FC<ServiceBookingSummaryProps> = ({ service }) => {
  const { token } = theme.useToken();
  const { t } = useTranslation('manage');

  if (!service) {
    return null;
  }

  const hasPadding = (service.paddingBeforeMinutes ?? 0) > 0 || (service.paddingAfterMinutes ?? 0) > 0;
  const statusHint =
    service.confirmationMode === 'DIRECT'
      ? t('appointmentComponents.create.service.statusConfirmed')
      : t('appointmentComponents.create.service.statusScheduled');

  return (
    <div
      style={{
        marginBottom: token.marginLG,
        padding: `${token.paddingSM}px ${token.paddingMD}px`,
        borderRadius: token.borderRadiusLG,
        background: token.colorFillQuaternary,
        border: `1px solid ${token.colorBorderSecondary}`
      }}
    >
      <Descriptions
        column={1}
        size="small"
        colon={false}
        styles={{
          label: { color: token.colorTextSecondary, fontSize: token.fontSizeSM, width: '38%' },
          content: { fontSize: token.fontSizeSM }
        }}
      >
        {service.description ? (
          <Descriptions.Item label={t('appointmentComponents.create.service.description')}>
            <Paragraph
              style={{ fontSize: token.fontSizeSM, marginBottom: 0 }}
              ellipsis={{ rows: 3, expandable: 'collapsible' }}
            >
              {service.description}
            </Paragraph>
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label={t('appointmentComponents.create.duration.label')}>
          {t('appointmentComponents.create.service.durationLocked', { count: service.durationMinutes })}
        </Descriptions.Item>
        {hasPadding ? (
          <Descriptions.Item label={t('appointmentComponents.create.service.padding')}>
            {t('appointmentComponents.create.service.paddingValue', {
              before: service.paddingBeforeMinutes,
              after: service.paddingAfterMinutes
            })}
          </Descriptions.Item>
        ) : null}
        <Descriptions.Item label={t('appointmentComponents.create.service.selectionMode')}>
          {serviceNeedsPrimaryResource(service)
            ? t('appointmentComponents.create.service.selectionClientPrimary')
            : t('appointmentComponents.create.service.selectionAutoAssign')}
        </Descriptions.Item>
        <Descriptions.Item label={t('appointmentComponents.create.service.bookingStatus')}>
          <Text strong style={{ fontSize: token.fontSizeSM }}>{statusHint}</Text>
        </Descriptions.Item>
      </Descriptions>
    </div>
  );
};
